import React, { useState, useEffect, createContext } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import { Theme } from './theme';
import createPersistedState from '@/hooks/use-persisted-state';

export const ThemeModes = {
  Dark: 'dark',
  Light: 'light',
};

// Detect user system color mode in browser
const usersSystemColorMode = window.matchMedia('(prefers-color-scheme: dark)')
  .matches
  ? ThemeModes.Dark
  : ThemeModes.Light;

export const ThemeContext = createContext();

// theme options
// 'default' : pick_pack style guide
// 'airport' : airport-app style guide
// 'vytal' : vytal style (defined through bootstrap variables)
function ThemeContextProvider({ children, initialTheme, setColorModeMantine }) {
  const [theme, setTheme] = useState(Theme[initialTheme] || Theme.default);

  // Color mode
  const colorModeStore = createPersistedState('colorModeStore');
  const [colorMode, setColorMode] = colorModeStore(usersSystemColorMode);

  useEffect(() => {
    if (window.location.hostname === 'fbb.pickpack.de') {
      setTheme(Theme.airport);
    }
  }, []);

  useEffect(() => {
    setColorModeMantine(colorMode);
    // Tailwind setup will check .dark
    if (colorMode === ThemeModes.Dark) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [colorMode]);

  const isDarkMode = () => {
    return colorMode === ThemeModes.Dark;
  };

  const isLightMode = () => {
    return colorMode === ThemeModes.Light;
  };

  return (
    <ThemeProvider theme={theme}>
      <ThemeContext.Provider
        value={{
          theme,
          setTheme,
          colorMode,
          setColorMode,
          isDarkMode: isDarkMode(),
          isLightMode: isLightMode(),
        }}
      >
        {children}
      </ThemeContext.Provider>
    </ThemeProvider>
  );
}

ThemeContextProvider.propTypes = {
  children: PropTypes.any.isRequired,
  initialTheme: PropTypes.string,
};

ThemeContextProvider.defaultProps = {
  initialTheme: 'default',
};

export default ThemeContextProvider;
